import React from 'react';
import { motion as Motion } from 'framer-motion';
import { Box, Typography, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Premium Loader Component
 * 
 * Premium loading component with multiple animation types:
 * - Holographic loader
 * - Particle loader
 * - Gradient loader
 * - Progress indicator support
 * - Multiple size options
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.type='holographic'] - Loader type
 * @param {string} [props.message] - Loading message (defaults to i18n 'common.loading')
 * @param {number} [props.progress=null] - Progress percentage (0-100)
 * @param {string} [props.size='medium'] - Loader size
 * @returns {JSX.Element} The premium loader component
 */
const PremiumLoader = ({ 
  type = 'holographic', 
  message = null, 
  progress = null,
  size = 'medium' 
}) => {
  const { t } = useTranslation();
  const displayMessage = message || t('common.loading', 'Loading');

  const sizeConfig = {
    small: { width: 60, height: 60, fontSize: '0.875rem' },
    medium: { width: 80, height: 80, fontSize: '1rem' },
    large: { width: 120, height: 120, fontSize: '1.25rem' }
  };

  const config = sizeConfig[size];

  // Animation holographique
  const HolographicLoader = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        padding: 4
      }}
    >
      <Motion.div
        style={{
          width: config.width,
          height: config.height,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        animate={{
          rotateY: [0, 360],
          scale: [1, 1.1, 1]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        {/* Outer holochart circle */}
        <Motion.div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: 'linear-gradient(45deg, #00ffff, #ff00ff, #ffff00, #00ffff)',
            backgroundSize: '300% 300%',
            opacity: 0.8,
            filter: 'blur(2px)'
          }}
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
            rotate: [0, 360]
          }}
          transition={{
            backgroundPosition: { duration: 3, repeat: Infinity },
            rotate: { duration: 4, repeat: Infinity, ease: "linear" }
          }}
        />
        
        {/* Inner circle */}
        <Motion.div
          style={{
            width: '70%',
            height: '70%',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.7, 1, 0.7]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <Typography
            variant="h6"
            sx={{
              background: 'linear-gradient(45deg, #00ffff, #ff00ff)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              fontWeight: 'bold',
              fontSize: config.fontSize
            }}
          >
            WMS
          </Typography>
        </Motion.div>
      </Motion.div>

      <Motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            fontSize: config.fontSize
          }}
        >
          {displayMessage}
        </Typography>
      </Motion.div>

      {progress !== null && (
        <Motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: 0.7 }}
          style={{ width: '200px' }}
        >
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #00ffff, #ff00ff)',
                borderRadius: 3
              }
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 1,
              color: 'text.secondary'
            }}
          >
            {Math.round(progress)}%
          </Typography>
        </Motion.div>
      )}
    </Box>
  );

  // Animation de particules
  const ParticleLoader = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        padding: 4
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: config.width,
          height: config.height
        }}
      >
        {[...Array(8)].map((_, i) => (
          <Motion.div
            key={i}
            style={{
              position: 'absolute',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: `hsl(${i * 45}, 70%, 60%)`,
              left: '50%',
              top: '50%',
              transformOrigin: `0 ${config.width / 2}px`
            }}
            animate={{
              rotate: [0, 360],
              scale: [1, 1.5, 1],
              opacity: [0.3, 1, 0.3]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.2,
              ease: "easeInOut"
            }}
          />
        ))}
        
        {/* Centre lumineux */}
        <Motion.div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: 'radial-gradient(circle, #ffffff, #00ffff)',
            boxShadow: '0 0 20px #00ffff'
          }}
          animate={{
            scale: [1, 1.3, 1],
            boxShadow: [
              '0 0 20px #00ffff',
              '0 0 40px #ff00ff',
              '0 0 20px #00ffff'
            ]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </Box>

      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          textAlign: 'center',
          fontSize: config.fontSize
        }}
      >
        {displayMessage}
      </Typography>
    </Box>
  );

  // Animation de morphing
  const MorphingLoader = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        padding: 4
      }}
    >
      <Motion.div
        style={{
          width: config.width,
          height: config.height,
          background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '20%'
        }}
        animate={{
          borderRadius: ['20%', '50%', '20%'],
          rotate: [0, 180, 360],
          scale: [1, 1.2, 1],
          background: [
            'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
            'linear-gradient(45deg, #f093fb 0%, #f5576c 100%)',
            'linear-gradient(45deg, #4facfe 0%, #00f2fe 100%)',
            'linear-gradient(45deg, #667eea 0%, #764ba2 100%)'
          ]
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      <Motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            fontSize: config.fontSize
          }}
        >
          {displayMessage}
        </Typography>
      </Motion.div>
    </Box>
  );

  // Animation de pulsation quantique
  const QuantumLoader = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        padding: 4
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: config.width,
          height: config.height
        }}
      >
        {[...Array(3)].map((_, i) => (
          <Motion.div
            key={i}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: `${100 - i * 20}%`,
              height: `${100 - i * 20}%`,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: `hsl(${200 + i * 60}, 70%, 60%)`,
              opacity: 0.7 - i * 0.2
            }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.7 - i * 0.2, 0, 0.7 - i * 0.2],
              rotate: [0, 360]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.3,
              ease: "easeInOut"
            }}
          />
        ))}
      </Box>

      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          textAlign: 'center',
          fontSize: config.fontSize
        }}
      >
        {displayMessage}
      </Typography>
    </Box>
  );

  const loaderComponents = {
    holographic: HolographicLoader,
    particle: ParticleLoader,
    morphing: MorphingLoader,
    quantum: QuantumLoader
  };

  const LoaderComponent = loaderComponents[type] || HolographicLoader;

  return (
    <Motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.3 }}
    >
      <LoaderComponent />
    </Motion.div>
  );
};

export default React.memo(PremiumLoader);

